import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";

export default function ConsoleLogin() {
  const { login, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  // Match the console light theme on global body / html to prevent dark bleed.
  useEffect(() => {
    const prevBody = document.body.style.backgroundColor;
    const prevHtml = document.documentElement.style.backgroundColor;
    document.body.style.backgroundColor = "#FAF7F1";
    document.documentElement.style.backgroundColor = "#FAF7F1";
    return () => {
      document.body.style.backgroundColor = prevBody;
      document.documentElement.style.backgroundColor = prevHtml;
    };
  }, []);

  useEffect(() => {
    if (user) {
      const dest = loc.state?.from?.pathname || ((user.role || "").toLowerCase() === "bd" ? "/outreach" : "/console/engagements");
      nav(dest, { replace: true });
    }
  }, [user, nav, loc.state]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      const dest = loc.state?.from?.pathname || ((u?.role || "").toLowerCase() === "bd" ? "/outreach" : "/console/engagements");
      nav(dest, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F1] flex" data-testid="page-console-login">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-white border-r border-[#E8E1D2] p-16 flex-col justify-between">
        <img
          src="/assets/logo/v2e-logo-light.png"
          alt="Voice2Evidence"
          className="h-10 w-auto self-start"
          data-testid="login-logo"
        />
        <div>
          <h1 className="font-serif text-5xl lg:text-6xl text-[#0F1825] leading-[1.05] tracking-tight">
            Patient voice,
            <span className="block italic">translated into evidence.</span>
          </h1>
          <p className="mt-6 text-[#5C6470] text-lg leading-relaxed max-w-md">
            A private workspace for the Voice2Evidence™ team and invited clients. Sign in to access the corporate briefing, the client playbook, live engagements, and the BD handbook.
          </p>
        </div>
        <div className="text-[12px] text-[#5C6470]/70 leading-relaxed">
          Authorised users only. All session activity is auditable.
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#C84C21]">Sign in</div>
            <h2 className="font-serif text-3xl text-[#0F1825] mt-3 leading-tight">Voice2Evidence sign-in</h2>
            <p className="mt-3 text-[#5C6470] text-[14px] leading-relaxed">
              Enter your Voice2Evidence credentials to continue.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="console-login-form">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6470] mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#E8E1D2] px-4 py-3 text-[#0F1825] font-mono text-[14px] focus:outline-none focus:border-[#C84C21] focus:ring-1 focus:ring-[#C84C21]/30"
                placeholder="name@marksmanhealthcare.com"
                data-testid="console-login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#5C6470] mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[#E8E1D2] px-4 py-3 text-[#0F1825] font-mono text-[14px] focus:outline-none focus:border-[#C84C21] focus:ring-1 focus:ring-[#C84C21]/30"
                data-testid="console-login-password"
                autoComplete="current-password"
              />
            </div>

            {err && (
              <div className="text-[13px] text-[#C84C21] font-mono uppercase tracking-[0.12em]" data-testid="console-login-error">{err}</div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-[#C84C21] hover:bg-[#A93D17] text-white font-mono text-[11px] uppercase tracking-[0.22em] px-6 py-3.5 transition-colors disabled:opacity-60"
              data-testid="console-login-submit"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
